import { ActivityParentEntityId } from "@sp-crm/core";
import { AddressDisplay } from "components/contact-display/address-display";
import { EmailDisplay } from "components/contact-display/email-display";
import { FaxDisplay } from "components/contact-display/fax-display";
import { PhoneDisplay } from "components/contact-display/phone-display";
import { WebsiteDisplay } from "components/contact-display/website-display";
import { ClampedText } from "components/ui/clamped-text";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";

export interface LabeledPhone {
    label: string | null;
    value: string;
}

export interface EntityOrganization {
    name: string;
    href: string;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
    address: string | null;
    phone: string | null;
    website: string | null;
    email: string | null;
}

export interface EntityDetails {
    classification: string | null;
    classificationLabel: string;
    phones: LabeledPhone[];
    fax: string | null;
    email: string | null;
    emailOptOut: boolean;
    address: string | null;
    website: string | null;
    organization: EntityOrganization | null;
}

export const present = (value: string | null | undefined): string | null => value || null;

export const labeledContactPhones = (phones: {
    cell: string | null | undefined;
    alternate: string | null | undefined;
}): LabeledPhone[] => {
    const cell = present(phones.cell);
    const alternate = present(phones.alternate);

    if (cell && alternate) {
        return [
            { label: "cell", value: cell },
            { label: "other", value: alternate },
        ];
    }

    const only = cell ?? alternate;

    return only ? [{ label: null, value: only }] : [];
};

const OrganizationContactDetails: React.FC<{
    organization: EntityOrganization;
}> = props => {
    const { organization } = props;

    return (
        <>
            {organization.phone ? (
                <PhoneDisplay
                    value={organization.phone}
                    label="office"
                    entityId={organization.entityId}
                    entityType={organization.entityType}
                />
            ) : null}
            {organization.email ? (
                <EmailDisplay
                    value={organization.email}
                    label="office"
                    optOut={false}
                    entityId={organization.entityId}
                    entityType={organization.entityType}
                />
            ) : null}
            {organization.website ? (
                <WebsiteDisplay value={organization.website} label="office" />
            ) : null}
        </>
    );
};

export const EntitySummary: React.FC<{ summary: string | null }> = props => {
    const { summary } = props;

    if (!summary) {
        return null;
    }

    return (
        <ClampedText lines={3}>
            <p className="whitespace-pre-wrap text-sm italic text-gray-500">{summary}</p>
        </ClampedText>
    );
};

export const hasEntityDetails = (details: EntityDetails): boolean =>
    !!(
        details.classification ||
        details.phones.length > 0 ||
        details.fax ||
        details.email ||
        details.address ||
        details.website ||
        details.organization
    );

interface EntityContactDetailsProps {
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
    details: EntityDetails;
}

export const EntityContactDetails: React.FC<EntityContactDetailsProps> = props => {
    const { entityId, entityType, details } = props;
    const { phones, fax, email, emailOptOut, address, website, organization } = details;

    if (!hasEntityDetails(details)) {
        return null;
    }

    return (
        <div className="flex flex-col items-start gap-x-5 gap-y-1.5 text-sm sm:flex-row sm:flex-wrap sm:items-center">
            {phones.map(phone => (
                <PhoneDisplay
                    key={phone.value}
                    value={phone.value}
                    label={phone.label}
                    entityId={entityId}
                    entityType={entityType}
                />
            ))}
            {fax ? <FaxDisplay value={fax} /> : null}
            {email ? (
                <EmailDisplay
                    value={email}
                    label={null}
                    optOut={emailOptOut}
                    entityId={entityId}
                    entityType={entityType}
                />
            ) : null}
            <AddressDisplay value={address} />
            {website ? <WebsiteDisplay value={website} label={null} /> : null}
            {organization ? (
                <OrganizationContactDetails organization={organization} />
            ) : null}
        </div>
    );
};

interface LabeledEntityContactDetailsProps {
    label: string;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
    details: EntityDetails;
}

export const LabeledEntityContactDetails: React.FC<
    LabeledEntityContactDetailsProps
> = props => {
    const { label, entityId, entityType, details } = props;

    if (!hasEntityDetails(details)) {
        return null;
    }

    return (
        <div className="flex flex-col gap-x-3 gap-y-0.5 sm:flex-row sm:items-center">
            <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                {label}
            </span>
            <EntityContactDetails
                entityId={entityId}
                entityType={entityType}
                details={details}
            />
        </div>
    );
};
